import { type ReactNode } from 'react';
import { Link } from '../../../components/Link';
import { PublicFooter } from '../../../components/PublicFooter';
import { PublicHeader } from '../../../components/PublicHeader';
import { Button, cn } from '../../../components/ui';
import { SITE_ORIGIN, Seo, breadcrumbFor, faqSchema } from '../../../lib/seo';
import { clusterOf, relatedFor, type LearnArticleMeta } from './learnArticles';

/**
 * Shared shell for every `/learn/*` article. Mirrors ToolPageLayout's split — the page module owns
 * the prose, this owns the chrome and the structured data — so an article is a content file and
 * nothing else.
 *
 * Four JSON-LD payloads per article:
 *   - TechArticle    → the article entity itself, with datePublished/dateModified and a publisher
 *                      pointing at the one Organization. This is the schema AI surfaces read for
 *                      attribution; DefinedTerm alone gave them no article to cite.
 *   - DefinedTerm    → names the concept and links it into the site's one DefinedTermSet.
 *   - FAQPage        → Google retired FAQ rich results in May 2026, so this earns no SERP dropdown
 *                      any more. Kept because assistants still extract Q/A pairs from it, and
 *                      because the questions are good content regardless.
 *   - BreadcrumbList → "Home › Learn › <term>".
 *
 * `inDefinedTermSet` is written as a TYPED node (@id + @type + name), not a bare `{"@id": …}`. A
 * lone @id is a reference to something the page must also declare, and the site-wide "no dangling
 * @id references" contract fails when it does not.
 *
 * THE LINK MATRIX IS DERIVED, not passed in. `relatedFor()` computes spoke → pillar, pillar → every
 * spoke, spoke ↔ 3 siblings, and one cross-cluster bridge from the registry. Hand-maintained link
 * rails rot on the third article; this one cannot.
 */
export const TERMSET_ID = `${SITE_ORIGIN}/learn/#termset`;
export const TERMSET_NAME = 'SieveGuard email deliverability glossary';

export interface LearnArticleContent {
  /** The registry entry — title, description, cluster, CTA and freshness all come from there. */
  meta: LearnArticleMeta;
  /**
   * The ~50-word answer, in plain prose. Rendered as the opening block AND emitted as
   * DefinedTerm.description, so it has to stand alone: this is the passage an AI Overview or an
   * assistant lifts, usually without the rest of the page.
   *
   * Plain string, not JSX, and deliberately free of angle brackets — it is serialised into a
   * `<script type="application/ld+json">` where a stray tag-ish sequence is a parsing hazard.
   */
  definition: string;
  /** The article body: `<h2>`/`<h3>` sections. The layout supplies h1, FAQ, CTA and rails. */
  body: ReactNode;
  /** Rendered as `<details>` AND emitted as FAQPage. Answers are plain text for that reason. */
  faq: Array<{ q: string; a: string }>;
}

export function LearnPageLayout({ content }: { content: LearnArticleContent }) {
  const { meta } = content;
  const url = `${SITE_ORIGIN}/learn/${meta.slug}/`;
  const cluster = clusterOf(meta.cluster);
  const related = relatedFor(meta.slug);

  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        path={`/learn/${meta.slug}`}
        keywords={[meta.term, 'email authentication', 'email deliverability'].join(', ')}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'TechArticle',
            '@id': `${url}#article`,
            headline: meta.title,
            description: meta.description,
            url,
            // Both dates present and equal until an article is genuinely revised. Emitting only
            // dateModified reads as "revised, origin unknown"; emitting neither leaves AI surfaces
            // with no freshness signal at all.
            datePublished: meta.updated,
            dateModified: meta.updated,
            inLanguage: 'en',
            isPartOf: { '@id': TERMSET_ID, '@type': 'DefinedTermSet', name: TERMSET_NAME },
            about: { '@id': `${url}#term`, '@type': 'DefinedTerm', name: meta.term },
            // References the site-wide Organization by @id rather than minting a second one.
            publisher: { '@id': `${SITE_ORIGIN}/#organization` },
            // Primary sources, machine-readable. "Citations to primary sources" is one of the
            // authority signals AI-search systems weigh; emitting them as CreativeWork nodes
            // rather than bare URLs is what lets a consumer tell a citation from a related link.
            ...(meta.citations?.length
              ? {
                  citation: meta.citations.map((c) => ({
                    '@type': 'CreativeWork',
                    name: c.title,
                    url: c.url,
                  })),
                }
              : {}),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'DefinedTerm',
            '@id': `${url}#term`,
            name: meta.term,
            description: content.definition,
            url,
            inDefinedTermSet: { '@id': TERMSET_ID, '@type': 'DefinedTermSet', name: TERMSET_NAME },
          },
          faqSchema(content.faq),
          breadcrumbFor([
            { name: 'Home', path: '/' },
            { name: 'Learn', path: '/learn' },
            { name: meta.term, path: `/learn/${meta.slug}` },
          ]),
        ]}
      />
      <div className="mx-auto max-w-[1180px] overflow-x-clip px-6 pb-20">
        <PublicHeader />

        <article className="mx-auto mt-8 max-w-3xl">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-faint">
            <Link to="/learn" className="transition hover:text-accent">
              Learn
            </Link>
            <span aria-hidden>/</span>
            <span className="text-muted">{cluster.name}</span>
          </div>

          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
            {meta.title}
          </h1>

          {/* The answer block. Visually distinct on purpose: it is the passage that has to work
              when it is the ONLY thing a reader (or an extractor) takes from the page. */}
          <p className="mt-6 rounded-2xl border border-accentdeep/40 bg-accentdeep/10 p-5 text-[15.5px] leading-relaxed">
            {content.definition}
          </p>

          {/* Provenance, visible rather than schema-only. Google's helpful-content guidance asks
              who wrote it, how, and why; for a technical reference the honest answer is "the team
              that runs the checks", and the checks themselves are the first-hand evidence. */}
          <p className="mt-4 text-[12.5px] text-faint">
            Written by the SieveGuard team from the checks our own free tools run.{' '}
            <span className="whitespace-nowrap">
              Last reviewed <time dateTime={meta.updated}>{formatDate(meta.updated)}</time>.
            </span>
          </p>

          <div className="mt-10 flex flex-col gap-4 text-[15px] leading-relaxed text-muted">
            {content.body}
          </div>

          {meta.citations && meta.citations.length > 0 && (
            <section className="mt-12">
              <h2 className="font-display text-lg font-bold tracking-tight">Sources</h2>
              <ul className="mt-4 flex flex-col gap-2">
                {meta.citations.map((c) => (
                  <li key={c.url} className="text-[13px] leading-relaxed">
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted underline underline-offset-4 transition hover:text-accent"
                    >
                      {c.title} ↗
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {content.faq.length > 0 && (
            <section className="mt-14">
              <h2 className="font-display text-2xl font-bold tracking-tight">FAQ</h2>
              <div className="mt-6 flex flex-col gap-2">
                {content.faq.map((f) => (
                  <details
                    key={f.q}
                    className="group rounded-2xl border border-bordersoft bg-panel p-5 transition open:bg-panel2"
                  >
                    <summary className="flex cursor-pointer items-center justify-between gap-3 text-[14.5px] font-semibold">
                      {f.q}
                      <span className="font-mono text-[12px] text-accent transition group-open:rotate-45">+</span>
                    </summary>
                    <p className="mt-3 text-[13.5px] leading-relaxed text-muted">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          <section className="mt-14 overflow-hidden rounded-3xl border border-accentdeep/40 bg-gradient-to-r from-accentdeep/10 to-panel/80 p-8 text-center">
            <h2 className="font-display text-2xl font-bold tracking-tight">Check it on a real domain</h2>
            <p className="mx-auto mt-3 max-w-md text-[14px] text-muted">
              Free, no signup, DNS-only. Nothing you type leaves the lookup.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to={meta.tool.to}>
                <Button variant="primary" className="h-12 px-6">{meta.tool.label}</Button>
              </Link>
              {meta.feature ? (
                <Link to={meta.feature.to}>
                  <Button variant="ghost" className="h-12 px-6">{meta.feature.label} →</Button>
                </Link>
              ) : (
                <Link to="/tools">
                  <Button variant="ghost" className="h-12 px-6">All free tools →</Button>
                </Link>
              )}
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-14">
              <h2 className="font-display text-lg font-bold tracking-tight">
                {meta.pillar ? `More on ${cluster.name.toLowerCase()}` : 'Keep reading'}
              </h2>
              {/* data-linkrail marks NAVIGATION for the near-duplicate guard in test/seo.spec.ts.
                  Every rail repeats its targets' titles and blurbs verbatim, so leaving it in a
                  content-similarity comparison makes the index page look like a 50% duplicate of
                  each article it links to. Boilerplate removal before comparison is what real
                  duplication analysis does; this attribute is how ours knows where to cut. */}
              <nav data-linkrail aria-label="Related articles" className="mt-5 grid gap-4 md:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/learn/${r.slug}`}
                    className="rounded-2xl border border-bordersoft bg-panel p-5 transition hover:border-accentdeep hover:bg-panel2"
                  >
                    <div className="text-[14px] font-semibold">{r.title} →</div>
                    <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">{r.blurb}</p>
                  </Link>
                ))}
              </nav>
            </section>
          )}
        </article>

        <PublicFooter />
      </div>
    </>
  );
}

/** "2026-08-04" → "4 August 2026". Pure string work — no Date, so SSR and client cannot disagree. */
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
export function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
}

/**
 * Section heading + body, so every article gets the same rhythm and — more to the point — the
 * same heading LEVELS. The site-wide contract fails on any h1 → h3 skip, and hand-written prose
 * is exactly where that creeps in.
 */
export function LearnSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-8 first:mt-0">
      <h2 className="font-display text-2xl font-bold tracking-tight text-txt">{title}</h2>
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </section>
  );
}

export function LearnSubsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6">
      <h3 className="font-display text-lg font-semibold tracking-tight text-txt">{title}</h3>
      <div className="mt-3 flex flex-col gap-4">{children}</div>
    </div>
  );
}

/** Monospace block for DNS records and headers. `overflow-x-auto` so it never widens the page. */
export function LearnCode({ children }: { children: ReactNode }) {
  return (
    <pre className="overflow-x-auto rounded-xl border border-bordersoft bg-panel2 p-4 font-mono text-[12.5px] leading-relaxed text-txt">
      {children}
    </pre>
  );
}

/** Inline monospace token — selector names, record labels, tag names. */
export function Tok({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-panel2 px-1.5 py-0.5 font-mono text-[13px] text-txt">{children}</code>
  );
}

/** Ordered/unordered list with the article's spacing. */
export function LearnList({ ordered, children }: { ordered?: boolean; children: ReactNode }) {
  const cls = cn('flex flex-col gap-2.5 pl-5', ordered ? 'list-decimal' : 'list-disc');
  return ordered ? <ol className={cls}>{children}</ol> : <ul className={cls}>{children}</ul>;
}

/** Bold lead-in inside a list item or paragraph. */
export function Lead({ children }: { children: ReactNode }) {
  return <strong className="text-txt">{children}</strong>;
}

/**
 * Data table. Every article is supposed to carry at least one thing nobody else publishes, and
 * that thing is almost always tabular — so the wrapper (with its horizontal scroll, which the
 * responsive contract requires) lives here rather than being retyped per article.
 */
export function LearnTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-bordersoft">
      <table className="w-full border-collapse text-left text-[13.5px]">
        <thead>
          <tr className="border-b border-bordersoft bg-panel2">
            {head.map((h) => (
              <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold text-txt">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-bordersoft/60 last:border-b-0">
              {r.map((cell, j) => (
                <td key={j} className={cn('px-4 py-3 align-top', j === 0 && 'text-txt')}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default LearnPageLayout;
